import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './MagicLinkAuth.css'

interface MagicLinkAuthProps {
    onAuthenticated: (sessionToken: string, partner: any) => void
}

const MagicLinkAuth: React.FC<MagicLinkAuthProps> = ({ onAuthenticated }) => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const [status, setStatus] = useState<'validating' | 'success' | 'error'>('validating')
    const [errorMessage, setErrorMessage] = useState('')

    useEffect(() => {
        const validateMagicLink = async () => {
            const token = searchParams.get('token')

            if (!token) {
                setStatus('error')
                setErrorMessage('No authentication token found')
                return
            }

            try {
                const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000'
                const response = await fetch(
                    `${apiUrl}/partners/validate-magic-link?token=${encodeURIComponent(token)}`,
                    {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    }
                )

                const data = await response.json()

                if (response.ok && data.success) {
                    setStatus('success')

                    // Store session token
                    const sessionToken = data.data.sessionToken
                    localStorage.setItem('partnerToken', sessionToken)

                    // Store partner info
                    localStorage.setItem('partner', JSON.stringify(data.data.partner))

                    // Call parent callback
                    onAuthenticated(sessionToken, data.data.partner)

                    // Redirect to docs after a short delay
                    setTimeout(() => {
                        navigate('/docs')
                    }, 1000)
                } else {
                    setStatus('error')
                    setErrorMessage(
                        data.error || data.message || 'Failed to validate magic link'
                    )
                }
            } catch (err) {
                console.error('Error validating magic link:', err)
                setStatus('error')
                setErrorMessage('Connection error. Please try again later.')
            }
        }

        validateMagicLink()
    }, [searchParams, navigate, onAuthenticated])

    return (
        <div className="magic-link-auth-container">
            <div className="magic-link-auth-card">
                {status === 'validating' && (
                    <>
                        <div className="spinner-large"></div>
                        <h2>Authenticating...</h2>
                        <p>Please wait while we validate your magic link</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="success-icon">
                            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                                <circle cx="32" cy="32" r="32" fill="#d1fae5" />
                                <path
                                    d="M20 32l8 8 16-16"
                                    stroke="#10b981"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h2>Authentication successful!</h2>
                        <p>Redirecting to documentation...</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="error-icon">
                            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                                <circle cx="32" cy="32" r="32" fill="#fee2e2" />
                                <path
                                    d="M24 24l16 16M40 24L24 40"
                                    stroke="#ef4444"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h2>Authentication failed</h2>
                        <p className="error-message">{errorMessage}</p>
                        <button
                            onClick={() => navigate('/')}
                            className="retry-button"
                        >
                            Request new link
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

export default MagicLinkAuth
